import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/node';

import {
  createRecentlyViewed,
  getRecentlyViewed,
  getSLIWishedProducts as getProducts,
  getRelatedProducts,
  getAlsoBoughtWithProducts,
  getProduct as fetchProduct,
  getProducts as fetchProducts
} from '../api';

import { generateMeta } from '../../utils/meta';
import { Page, Content } from '../../theme/page';
import { trackProductView } from '../../utils/tracking';
import getCookie from '../../utils/getCookie';
import hasError from '../../utils/hasError';

import { Skeleton, SkeletonWrapper } from '../../atoms/skeleton';
import BreadCrumbs from '../../molecules/breadcrumbs';
import ProductCardCarousel from '../../organs/product-card-carousel';
import ProductCarousel from '../../tissues/product-carousel';
import ProductDetails from '../../organs/pdp-details';
import ErrorPage from '../_error';
import SCOPE_TYPES from '../../utils/sentryScopeTypes';

const Separator = styled.hr`
  background: ${(props) => props.theme.color.brand};
  border: none;
  margin: 3rem 0;
  width: 100%;
  height: 1px;
`;

const DetailsSkeletonWrapper = styled(SkeletonWrapper)`
  grid-area: product-information;
`;

const CarouselSkeletonWrapper = styled(SkeletonWrapper)`
display: flex;
  grid-area: product-carousel;

  ${Skeleton}::before {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
`;

const BreadcrumbSkeletonWrapper = styled(SkeletonWrapper)`
  grid-area: breadcrumbs;

  ${Skeleton} {
    height: 1.5rem;
    margin-bottom: 3rem;
  }
`;

const DetailsSkeleton = () => (
  <DetailsSkeletonWrapper>
    {/* Product Header */}
    <Skeleton css={{ height: '12rem', marginBottom: '3rem' }} />

    {/* Product Information */}
    <Skeleton css={{ height: '35rem', marginBottom: '3rem' }} />

    {/* Product Options */}
    <Skeleton css={{ height: '30rem', marginBottom: '3rem' }} />

    {/* Product Options */}
    <Skeleton css={{ height: '7rem', marginBottom: '3rem' }} />
  </DetailsSkeletonWrapper>
);

const CarouselSkeleton = () => (
  <CarouselSkeletonWrapper>
    <Skeleton />
  </CarouselSkeletonWrapper>
);

const BreadcrumbSkeleton = () => (
  <BreadcrumbSkeletonWrapper>
    <Skeleton />
  </BreadcrumbSkeletonWrapper>
);

const DEFAULT_META = {
  key: 'product',
  title: 'Maisonette',
  description: 'Maisonette',
  og: {
    description: 'Maisonette',
    image: null,
    title: 'Maisonette',
    url: global.window?.location
  },
  twitter: {
    description: 'Maisonette',
    image: null,
    title: 'Maisonette'
  }
};

const Product = styled.div`
  display: grid;
  grid-gap: 3rem;
  grid-template-areas: 'breadcrumbs' 'product-carousel' 'product-information';
  grid-template-columns: minmax(0, 1fr);
  margin-bottom: 3rem;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    grid-gap: 5rem;
    grid-template-columns: 50%;
    grid-template-areas: 'breadcrumbs breadcrumbs' 'product-carousel product-information';
  }
`;

const ProductDetailPage = (props) => {
  const [recentlyViewed, setRecentlyViewed] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState(false);
  const [alsoBoughtWith, setAlsoBoughtWith] = useState(false);

  const { asPath, isFallback } = useRouter();

  const setProductAsRecentlyViewed = async (variant_id) => {
    const session_id = getCookie('maisonette_session_token');

    await createRecentlyViewed({
      body: {
        recently_viewed: {
          session_id,
          variant_id
        }
      }
    });
  };

  const getRecentlyViewedProducts = () => {
    const session_id = getCookie('maisonette_session_token');

    getRecentlyViewed({ session_id })
      .then(async (res) => {
        if (hasError(res)) {
          setRecentlyViewed(false);
          return;
        }

        try {
          const ids = res.map((x) => x.variant_id);

          const sliProducts = await getProducts({ ids });

          const sortSliRes = () => {
            const products = sliProducts.results;
            const recent = res.map((x) => x.variant_id);

            return products.sort((a, b) => (
              recent.indexOf(+a.product_id_wishlist) - recent.indexOf(+b.product_id_wishlist)
            ));
          };

          setRecentlyViewed({
            ...sliProducts,
            results: sortSliRes()
              .filter((x) => +x.product_id_wishlist !== +props.product.master.id).slice(0, 20)
          });
        } catch (error) {
          setRecentlyViewed(false);
        }
      });
  };

  useEffect(() => {
    if (props.product && !hasError(props.product)) {
      setRelatedProducts(false);
      setAlsoBoughtWith(false);
      setProductAsRecentlyViewed(props.product.master.id);

      trackProductView({ product: props.product });

      getRelatedProducts({ sku: props.product?.master?.sku, count: 15 })
        .then((res) => {
          if (hasError(res)) return;
          setRelatedProducts(res);
        });

      getAlsoBoughtWithProducts({ sku: props.product?.master?.sku, count: 15 })
        .then((res) => {
          if (hasError(res)) return;
          setAlsoBoughtWith(res);
        });

      getRecentlyViewedProducts();
    }
  }, [props.product]);

  if (isFallback) {
    return (
      <>
        <Head>
          {generateMeta(DEFAULT_META)}
        </Head>

        <Page id="maincontent">
          <Content>
            <Product>
              <BreadcrumbSkeleton />
              <CarouselSkeleton />
              <DetailsSkeleton />
            </Product>
          </Content>
        </Page>
      </>
    );
  }

  if (!props.product || hasError(props.product)) {
    return <ErrorPage statusCode={404} />;
  }

  const hasImages = props.product?.master && props.product?.master?.images;

  const meta = (props.product && props.product?.breadcrumb_taxons) ? {
    key: 'product',
    title: `${props.product.name} - ${props.product.breadcrumb_taxons.map((bc) => bc.name).join(' ')} - Maisonette`,
    description: `${props.product.description} - ${props.product.breadcrumb_taxons.map((bc) => bc.name).join(' ')} - Maisonette`,
    og: {
      description: `${props.product.description} - ${props.product.breadcrumb_taxons.map((bc) => bc.name).join(' ')} - Maisonette`,
      image: props.product.master.images.length > 0
        ? props.product.master.images[0]?.large_url ?? null
        : null,
      title: `${props.product.name} - ${props.product.breadcrumb_taxons.map((bc) => bc.name).join(' ')} - Maisonette`,
      url: `${process.env.NEXT_PUBLIC_CLIENT_HOST}${asPath}`
    },
    twitter: {
      description: `${props.product.description} - ${props.product.breadcrumb_taxons.map((bc) => bc.name).join(' ')} - Maisonette`,
      image: props.product.master.images.length > 0
        ? props.product.master.images[0]?.large_url ?? null
        : null,
      title: `${props.product.name} - ${props.product.breadcrumb_taxons.map((bc) => bc.name).join(' ')} - Maisonette`
    }
  } : DEFAULT_META;

  return (
    <div key={props.product.id}>
      <Head>
        {generateMeta(meta)}
      </Head>

      <Page id="maincontent">
        <Content>
          <Product>
            <BreadCrumbs breadcrumbs={props.product?.breadcrumb_taxons || []} />

            {
              hasImages && (
                <ProductCarousel
                  images={props.product?.master?.images}
                  name={props.product?.master?.name}
                />
              )
            }

            <ProductDetails product={props.product} />
          </Product>

          {
            alsoBoughtWith && (
              <ProductCardCarousel
                title="Also Bought With"
                products={alsoBoughtWith}
                id="also-bought-with"
                trackFor="Also Bought"
                isInfinite
              />
            )
          }

          {
            (
              alsoBoughtWith?.results?.length > 0
              && relatedProducts?.results?.length > 0
            ) ? <Separator /> : null
          }

          {
            relatedProducts && (
              <ProductCardCarousel
                title="Related Products"
                products={relatedProducts}
                id="related-products"
                trackFor="Related Products"
                isInfinite
              />
            )
          }

          {
            (
              relatedProducts?.results?.length > 0
              && recentlyViewed?.results?.length > 0
            ) ? <Separator /> : null
          }

          {
            recentlyViewed && (
              <ProductCardCarousel
                title="Recently Viewed"
                products={recentlyViewed}
                id="recently-viewed"
              />
            )
          }
        </Content>
      </Page>
    </div>
  );
};

export async function getStaticPaths() {
  const paths = [];

  if (process.env.NEXT_PUBLIC_SENTRY_ENV !== 'ci') {
    try {
      // SLI returns max 300 items
      const products = await fetchProducts({
        base: process.env.SLI_HOST,
        uri: '/search',
        method: 'GET',
        queryParams: {
          ts: 'json-full',
          isort: 'globalpop',
          w: '*',
          cnt: 300
        },
        scopes: [SCOPE_TYPES.SERVICES.SLI]
      });

      products.results.map(({ product_slug }) => paths.push({ params: { slug: product_slug } }));
    } catch (error) { /* noop */ }
  }

  return {
    paths,
    fallback: true
  };
}

export async function getStaticProps({ params }) {
  const { slug } = params;

  const getProduct = (id) => fetchProduct({
    base: process.env.SOLIDUS_HOST,
    uri: `/api/products/${id}`,
    scopes: [SCOPE_TYPES.SERVICES.SOLIDUS]
  });

  // if there was an error fetching product
  // - return null for product,
  // - revalidation will try to fetch again on next attempt
  const product = await getProduct(slug)
    .catch((error) => {
      Sentry.withScope((scope) => {
        scope.setContext('slug', slug);
        scope.setFingerprint('/pages/product/[slug]::getStaticProps');
        Sentry.captureException(error);
      });

      return null;
    });

  if (product && hasError(product)) return { notFound: true };

  return {
    props: { product },
    // next.js will attempt to re-generate the page:
    // - when a request comes in
    // - at most once every minute
    revalidate: 60 // in seconds
  };
}

ProductDetailPage.defaultProps = {
  product: null
};

ProductDetailPage.propTypes = {
  product: PropTypes.object
};

export default ProductDetailPage;
